import React, { memo } from 'react';
import {
  Autocomplete, Box, Chip, FormControlLabel, IconButton, Stack, Switch,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';

import type { InterviewRubricItem, RubricItemKind } from '../../../types/admissions';
import { segmentedSx } from '../tabLayout';
import { CopyToAllButton, MoveButtons } from './sectionParts';

interface Props {
  item: InterviewRubricItem;
  index: number;
  /** Questions on the sheet, for the reorder arrows. */
  count: number;
  /** More than one scored question, so marks can be copied across. */
  canCopyMarks: boolean;
  onChange: (index: number, patch: Partial<InterviewRubricItem>) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onCopyMarks: (index: number) => void;
  onRemove: (index: number) => void;
}

export const RUBRIC_KIND_LABEL: Record<RubricItemKind, string> = {
  marks: 'Marks',
  choice: 'Choice',
  note: 'Note',
};

const KIND_HINT: Record<RubricItemKind, string> = {
  marks: 'Scored — counts toward the round total',
  choice: 'Pick one option — recorded, not scored',
  note: 'Free text — recorded, not scored',
};

/**
 * One question on a round's sheet, in the round editor: the question, what
 * kind of answer it takes, and that kind's own setting — max marks, the
 * options to pick from, or whether an answer is required.
 */
const RubricItemRow = memo<Props>(({ item, index, count, canCopyMarks, onChange, onMove, onCopyMarks, onRemove }) => {
  const kind = item.kind ?? 'marks';

  const setKind = (next: RubricItemKind | null) => {
    if (!next || next === kind) return;
    onChange(index, {
      kind: next,
      maxMarks: next === 'marks' ? (item.maxMarks || 10) : 0,
      options: next === 'choice' ? (item.options?.length ? item.options : ['Yes', 'No']) : undefined,
      required: next === 'marks' ? undefined : item.required,
    });
  };

  const actions = (
    <Stack direction="row" alignItems="center" sx={{ color: 'text.secondary', flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}>
      {/* Fixed slot, so rows line up whether or not marks can be copied. */}
      <Box sx={{ width: 34, display: 'flex', justifyContent: 'center' }}>
        {kind === 'marks' && canCopyMarks && (
          <CopyToAllButton onClick={() => onCopyMarks(index)} title="Use these marks for every scored question" />
        )}
      </Box>
      <MoveButtons index={index} count={count} onMove={(dir) => onMove(index, dir)} />
      <Tooltip title="Remove question">
        <IconButton size="small" onClick={() => onRemove(index)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  // Wide screens: one line. Phones: the question on its own line, then the
  // answer type and marks, with the actions wrapping right when they don't fit.
  return (
    <Box sx={{ py: 1.25, '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder={kind === 'choice' ? 'Question (e.g. Interview attended by)' : kind === 'note' ? 'Question (e.g. Interviewer remarks)' : 'Question (e.g. Communication — child)'}
          fullWidth
          value={item.criterion}
          onChange={(e) => onChange(index, { criterion: e.target.value })}
        />
        <Stack direction="row" alignItems="center" useFlexGap sx={{ flexShrink: 0, columnGap: 1, rowGap: 0.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <Tooltip title={KIND_HINT[kind]}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={kind}
              onChange={(_, v) => setKind(v)}
              aria-label="Answer type"
              sx={[segmentedSx, { flexWrap: 'nowrap' }]}
            >
              {(Object.keys(RUBRIC_KIND_LABEL) as RubricItemKind[]).map(k => (
                <ToggleButton key={k} value={k}>{RUBRIC_KIND_LABEL[k]}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Tooltip>
          <Box sx={{ width: 104, display: 'flex', justifyContent: 'center' }}>
            {kind === 'marks' ? (
              <TextField
                size="small"
                placeholder="Max"
                type="number"
                inputProps={{ min: 0.5, step: 0.5, 'aria-label': 'Max marks' }}
                value={item.maxMarks || ''}
                onChange={(e) => onChange(index, { maxMarks: Number(e.target.value) || 0 })}
                sx={{ width: 104 }}
              />
            ) : (
              <FormControlLabel
                control={<Switch size="small" checked={!!item.required} onChange={(e) => onChange(index, { required: e.target.checked })} />}
                label="Required"
                slotProps={{ typography: { variant: 'caption', color: 'text.secondary' } }}
                sx={{ m: 0 }}
              />
            )}
          </Box>
          {actions}
        </Stack>
      </Stack>
      {kind === 'choice' && (
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={item.options || []}
          onChange={(_, v) => onChange(index, { options: Array.from(new Set((v as string[]).map(o => o.trim()).filter(Boolean))) })}
          renderTags={(value, getTagProps) => value.map((option, i) => (
            <Chip size="small" variant="outlined" label={option} {...getTagProps({ index: i })} key={option} />
          ))}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={(item.options || []).length ? 'Add another option…' : 'Type an option and press Enter…'}
              helperText={(item.options || []).length < 2 ? 'Add at least two options.' : undefined}
              error={(item.options || []).length < 2}
            />
          )}
          sx={{ mt: 1, ml: { sm: 0 } }}
        />
      )}
    </Box>
  );
});

RubricItemRow.displayName = 'RubricItemRow';

export default RubricItemRow;
